<script setup lang="ts">
import { computed } from 'vue'
import { NTooltip } from 'naive-ui'
import { useFormattedDate } from '../composables/useFormattedDate'
import { useNow } from '../composables/useNow'
import { formatBytes } from '../composables/formatBytes'
import { compareCliVersions, type CliVersionStatus } from '../composables/cliVersionStatus'
import type { AgentRepoStatus, DevcontainerStatus } from '../stores/agent_repos'

// The live devcontainer runtime section of a repo's status grid in
// AdminAgentRepos.vue — extracted (#843) so that parent stays under the
// 300-line hard limit as resource metrics (CPU/mem/disk, #843) join the
// status/last-activity fields (#842). Renders as `dt`/`dd` pairs (a Vue 3
// multi-root component), so it slots directly into the parent's `<dl>`
// grid without an extra wrapper element.
const props = defineProps<{ repo: AgentRepoStatus; latestCliVersion: string | null }>()

const { formatDate, formatAbsoluteDate } = useFormattedDate()
const now = useNow()

const STATUS_LABELS: Record<DevcontainerStatus, string> = { running: 'Running', idle: 'Idle', stopped: 'Stopped' }
const STATUS_CLASSES: Record<DevcontainerStatus, string> = {
  running: 'text-brand-primary',
  idle: 'text-ink-muted',
  stopped: 'text-ink-faint',
}

// Neutral (no color) when there's nothing yet to compare against — the
// repo's never been checked, or the latest version itself is unknown.
const CLI_VERSION_STATUS_CLASSES: Record<CliVersionStatus, string> = {
  match: 'text-brand-primary',
  'patch-behind': 'text-factory',
  diverged: 'text-status-alert',
}

// Whether the running container's image predates a later `.devcontainer/**`
// change (#1123). Neutral/"Unknown" for null — covers both "stopped, nothing
// to compare" and "running but genuinely unknown" alike, same as the CLI
// version status above.
const devcontainerStaleLabel = computed(() => {
  if (props.repo.devcontainer_stale === null) return 'Unknown'
  return props.repo.devcontainer_stale ? 'Stale' : 'Fresh'
})
const devcontainerStaleClass = computed(() => {
  if (props.repo.devcontainer_stale === null) return 'text-ink-faint'
  return props.repo.devcontainer_stale ? 'text-factory' : 'text-brand-primary'
})

const cpuLabel = computed(() => (props.repo.cpu_percent == null ? '—' : `${props.repo.cpu_percent.toFixed(1)}%`))
const memoryLabel = computed(() => formatBytes(props.repo.memory_bytes))
const diskLabel = computed(() => formatBytes(props.repo.disk_bytes))
const cliVersionLabel = computed(() => props.repo.cli_version ?? '—')
const cliVersionStatus = computed(() => compareCliVersions(props.repo.cli_version, props.latestCliVersion))
const cliVersionClass = computed(() =>
  cliVersionStatus.value ? CLI_VERSION_STATUS_CLASSES[cliVersionStatus.value] : 'text-ink-faint',
)

// Last outcome of the nightly AgentCliAutoUpdateJob (#896) for this repo —
// distinct from the manual "Update Claude Code CLI" button, which doesn't
// record anything here.
const lastAutoUpdateClass = computed(() =>
  props.repo.last_auto_update_status === 'failed' ? 'text-status-alert' : 'text-brand-primary',
)

// Outcome of the last start/stop/reboot/rebuild/update_cli (#1051). Worth
// its own row rather than folding into Status above: these actions run for
// minutes as a background job, so without this the page would show a
// container that is up and give no hint that the reboot which produced it
// actually failed.
const LIFECYCLE_STATUS_CLASSES: Record<string, string> = {
  queued: 'text-ink-muted',
  running: 'text-factory',
  succeeded: 'text-brand-primary',
  failed: 'text-status-alert',
}
const lifecycleClass = computed(() => LIFECYCLE_STATUS_CLASSES[props.repo.lifecycle_status ?? ''] ?? 'text-ink-faint')
const lifecycleLabel = computed(() => {
  const { lifecycle_action: action, lifecycle_status: status } = props.repo
  return action && status ? `${action} — ${status}` : 'None'
})

// `lifecycle_error` also carries a note on a SUCCEEDED row (#1236) — the
// first attempt failed and ComposeFailureDiagnostic verified it recovered.
// Still worth flagging, but red reads as "this action is broken", which
// isn't true here — amber matches how `running`/in-flight states read above.
const lifecycleNoteClass = computed(() =>
  props.repo.lifecycle_status === 'failed' ? 'text-status-alert' : 'text-factory',
)

// CPU/memory/disk/CLI version/image staleness above are measured by a job on
// the agent box, not read live per request, so their age is part of the
// reading. Amber once they're old enough that the box has plausibly stopped
// refreshing them — a sweep runs about once a minute.
const STALE_METRICS_AFTER_MS = 5 * 60 * 1000
const metricsAgeClass = computed(() => {
  if (!props.repo.stats_refreshed_at) return 'text-ink-faint'
  const age = Date.now() - new Date(props.repo.stats_refreshed_at).getTime()
  return age > STALE_METRICS_AFTER_MS ? 'text-factory' : 'text-ink-muted'
})
</script>

<template>
  <dt class="text-ink-faint">Status</dt>
  <dd
    class="m-0 flex items-center gap-1.5"
    :class="STATUS_CLASSES[repo.container_status]"
    :data-testid="`agent-repo-devcontainer-status-${repo.slug}`"
  >
    <span
      v-if="repo.container_status === 'running'"
      class="shrink-0 w-1.5 h-1.5 rounded-full bg-brand-primary animate-pulse"
      aria-hidden="true"
    />
    {{ STATUS_LABELS[repo.container_status] }}
  </dd>
  <dt class="text-ink-faint">Last activity</dt>
  <!-- NTooltip on the relative timestamps rather than a native title:
       reachable by keyboard focus and consistent with the page's hover
       help idiom (same for the other timestamp rows below). -->
  <NTooltip v-if="repo.last_activity_at" placement="top">
    <template #trigger>
      <dd
        class="m-0 text-ink-muted tabular-nums cursor-help"
        tabindex="0"
        :data-testid="`agent-repo-last-activity-${repo.slug}`"
      >
        {{ formatDate(repo.last_activity_at, now) }}
      </dd>
    </template>
    {{ formatAbsoluteDate(repo.last_activity_at) }}
  </NTooltip>
  <dd v-else class="m-0 text-ink-faint" :data-testid="`agent-repo-last-activity-${repo.slug}`">Never</dd>
  <dt class="text-ink-faint">Agent session</dt>
  <dd
    class="m-0 flex items-center gap-1.5"
    :class="STATUS_CLASSES[repo.agent_session_status]"
    :data-testid="`agent-repo-session-status-${repo.slug}`"
  >
    <span
      v-if="repo.agent_session_status === 'running'"
      class="shrink-0 w-1.5 h-1.5 rounded-full bg-brand-primary animate-pulse"
      aria-hidden="true"
    />
    {{ STATUS_LABELS[repo.agent_session_status] }}
  </dd>
  <dt class="text-ink-faint">CPU</dt>
  <dd class="m-0 text-ink-muted tabular-nums" :data-testid="`agent-repo-cpu-${repo.slug}`">{{ cpuLabel }}</dd>
  <dt class="text-ink-faint">Memory</dt>
  <dd class="m-0 text-ink-muted tabular-nums" :data-testid="`agent-repo-memory-${repo.slug}`">{{ memoryLabel }}</dd>
  <dt class="text-ink-faint">Disk</dt>
  <dd class="m-0 text-ink-muted tabular-nums" :data-testid="`agent-repo-disk-${repo.slug}`">{{ diskLabel }}</dd>
  <dt class="text-ink-faint">CLI version</dt>
  <dd class="m-0 tabular-nums" :class="cliVersionClass" :data-testid="`agent-repo-cli-version-${repo.slug}`">
    {{ cliVersionLabel }}
  </dd>
  <dt class="text-ink-faint">Devcontainer image</dt>
  <dd class="m-0" :class="devcontainerStaleClass" :data-testid="`agent-repo-devcontainer-stale-${repo.slug}`">
    {{ devcontainerStaleLabel }}
  </dd>
  <dt class="text-ink-faint">Metrics as of</dt>
  <NTooltip v-if="repo.stats_refreshed_at" placement="top">
    <template #trigger>
      <dd
        class="m-0 tabular-nums cursor-help"
        :class="metricsAgeClass"
        tabindex="0"
        :data-testid="`agent-repo-stats-refreshed-${repo.slug}`"
      >
        {{ formatDate(repo.stats_refreshed_at, now) }}
        <span
          v-if="repo.stats_error"
          class="block text-xs text-status-alert break-words"
          :data-testid="`agent-repo-stats-error-${repo.slug}`"
          >{{ repo.stats_error }}</span
        >
      </dd>
    </template>
    {{ formatAbsoluteDate(repo.stats_refreshed_at) }}
  </NTooltip>
  <dd v-else class="m-0 text-ink-faint" :data-testid="`agent-repo-stats-refreshed-${repo.slug}`">Not yet measured</dd>
  <dt class="text-ink-faint">Last auto-update</dt>
  <NTooltip v-if="repo.last_auto_update_at" placement="top">
    <template #trigger>
      <dd
        class="m-0 cursor-help"
        :class="lastAutoUpdateClass"
        tabindex="0"
        :data-testid="`agent-repo-last-auto-update-${repo.slug}`"
      >
        {{ formatDate(repo.last_auto_update_at, now) }} — {{ repo.last_auto_update_status }}
      </dd>
    </template>
    {{ formatAbsoluteDate(repo.last_auto_update_at) }}
  </NTooltip>
  <dd v-else class="m-0 text-ink-faint" :data-testid="`agent-repo-last-auto-update-${repo.slug}`">Never</dd>
  <dt class="text-ink-faint">Last action</dt>
  <!-- No title here: the full lifecycle_error already renders inline in the
       span below, so a hover-only duplicate added nothing accessible. -->
  <dd class="m-0" :class="lifecycleClass" :data-testid="`agent-repo-lifecycle-${repo.slug}`">
    {{ lifecycleLabel }}
    <span
      v-if="repo.lifecycle_error"
      class="block text-xs break-words"
      :class="lifecycleNoteClass"
      :data-testid="`agent-repo-lifecycle-error-${repo.slug}`"
      >{{ repo.lifecycle_error }}</span
    >
  </dd>
</template>
